/**
 * @file   src/components/Inventory/EditInventory/InventoryPropertyTypes.js
 * @brief  Two-level RESO property category + type selection for Inventory creation/edit.
 *         Both category and type use single-select radios (unlike Want-Listings which use
 *         multi-select checkboxes for type).
 * @date   February, 2026
 */
import React, { useState, useEffect } from "react";
import _ from "lodash";
import { getPropertyCategoriesWithTypes } from "../../../services/commonServices";
import {
  PROPERTY_TYPE_TO_CATEGORY,
} from "../../../constants/common";
import { convertToNumber } from "../../../helpers/validations";

const InventoryPropertyTypes = (props) => {
  const {
    handleFormValues,
    propertyCategory,
    propertyType,
    clearPreviousStates,
    propertyTypeError,
  } = props;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Fetch categories and their types on mount
  useEffect(() => {
    getPropertyCategoriesWithTypes("buy")
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const cats = _.get(response, "result", null);
          setCategories(cats);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, []);

  // Infer category from existing propertyType on edit (backwards compat)
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      if (propertyCategory) {
        setSelectedCategory(propertyCategory);
      } else if (propertyType) {
        const inferredCategory = PROPERTY_TYPE_TO_CATEGORY[propertyType];
        if (inferredCategory) {
          setSelectedCategory(inferredCategory);
          // Pre-RESO properties have no stored category; persist the inferred
          // one so the form (and its required-field check) matches the UI.
          handleFormValues({
            propertyCategory: inferredCategory,
            propertyCategoryTxt: _.get(
              categories.find((c) => c.CategoryId === inferredCategory),
              "CategoryText",
              ""
            ),
          });
        }
      }
    }
  }, [categories, propertyCategory, propertyType]);

  /**
   * Handle category radio selection
   */
  const handleCategorySelect = (categoryId, categoryText) => {
    const isSameCategory = convertToNumber(categoryId) === convertToNumber(selectedCategory);
    if (isSameCategory) return;

    setSelectedCategory(categoryId);
    clearPreviousStates();
    handleFormValues({
      propertyCategory: categoryId,
      propertyCategoryTxt: categoryText,
      prprtyType: "",
      prptyTypeTxt: "",
      prptySubType: "",
    });
  };

  /**
   * Handle property type radio selection within selected category
   */
  const handleTypeSelect = (typeId, typeText) => {
    const isSameType = convertToNumber(typeId) === convertToNumber(propertyType);
    if (isSameType) return;

    handleFormValues({
      prprtyType: typeId,
      prptyTypeTxt: typeText,
      prptySubType: "",
    });
  };

  // Get the types for the currently selected category
  const selectedCategoryData = categories.find(
    (c) => c.CategoryId === selectedCategory
  );

  return (
    <>
      {/* Property Category - radio selection */}
      <h6 className={propertyTypeError && !selectedCategory ? "red-marked-subheading" : ""}>
        Property Category<span>*</span>
      </h6>
      <div className="wantad-type-wrap mb-3">
        {categories.map((cat) => (
          <label className="radio-wrap mb-3" key={cat.CategoryId}>
            <input
              type="radio"
              name="inventory_category_radio"
              value={cat.CategoryId}
              checked={selectedCategory === cat.CategoryId}
              onChange={() =>
                handleCategorySelect(cat.CategoryId, cat.CategoryText)
              }
            />
            <span className={propertyTypeError && !selectedCategory ? "red-marked-span" : ""}>
              <div className="wantad-type-info">
                <h2
                  className={
                    propertyTypeError && !selectedCategory
                      ? "pb-0 red-marked-subheading"
                      : "pb-0"
                  }
                >
                  {cat.CategoryText}
                </h2>
              </div>
            </span>
          </label>
        ))}
      </div>

      {/* Property Type - radio single-select (shown after category selected) */}
      {selectedCategoryData && (
        <>
          <h6
            className={
              propertyTypeError && selectedCategory && !propertyType
                ? "red-marked-subheading"
                : ""
            }
          >
            Property Type<span>*</span>
          </h6>
          <div className="wantad-type-wrap">
            {selectedCategoryData.types.map((pt) => {
              const typeId = pt.PropertyTypeId;
              const isChecked = convertToNumber(propertyType) === convertToNumber(typeId);
              return (
                <label className="radio-wrap mb-3" key={typeId}>
                  <input
                    type="radio"
                    name="inventory_type_radio"
                    value={typeId}
                    checked={isChecked}
                    onChange={() => handleTypeSelect(typeId, pt.PropertyTypeText)}
                  />
                  <span
                    className={
                      propertyTypeError && selectedCategory && !propertyType
                        ? "red-marked-span"
                        : ""
                    }
                  >
                    <div className="wantad-type-info">
                      <h2
                        className={
                          propertyTypeError && selectedCategory && !propertyType
                            ? "pb-0 red-marked-subheading"
                            : "pb-0"
                        }
                      >
                        {pt.PropertyTypeText}
                      </h2>
                    </div>
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default InventoryPropertyTypes;
